import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Delete,
  Download,
  NoteAdd,
  RestoreFromTrash,
} from "@mui/icons-material";
import {
  DOCUMENT_AUDIT_EVENT_LABELS,
  DOCUMENT_ENTITY_TYPE_LABELS,
  DOCUMENT_TYPE_LABELS,
} from "@/api/labels";
import {
  useDeleteDocument,
  useDocument,
  useDownloadVersion,
  useRestoreDocument,
  useUpdateDocument,
} from "@/api/hooks/documents";
import ReasonPromptDialog from "@/components/documents/ReasonPromptDialog";
import { formatBytes, formatDateTime } from "@/utils/format";

type ModalAction = "edit" | "delete" | "restore" | null;

export default function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: doc, isLoading, error } = useDocument(id);
  const update = useUpdateDocument(id ?? "");
  const remove = useDeleteDocument();
  const restore = useRestoreDocument();
  const download = useDownloadVersion();

  const [modal, setModal] = useState<ModalAction>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (isLoading) return <CircularProgress />;
  if (error || !doc)
    return <Alert severity="error">{error?.message ?? "Document not found."}</Alert>;

  const handleDownload = async (versionId: string) => {
    setDownloadError(null);
    try {
      const res = await download.mutateAsync({ documentId: doc.id, versionId });
      window.location.href = res.download_url;
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/clinops/documents")} aria-label="Back">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{doc.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {DOCUMENT_TYPE_LABELS[doc.document_type]} ·{" "}
            {DOCUMENT_ENTITY_TYPE_LABELS[doc.entity_type]}
            {doc.deleted_at && (
              <Chip
                label="Deleted"
                color="error"
                variant="outlined"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {doc.deleted_at ? (
            <Button
              startIcon={<RestoreFromTrash />}
              color="warning"
              onClick={() => setModal("restore")}
            >
              Restore
            </Button>
          ) : (
            <>
              <Button startIcon={<NoteAdd />} onClick={() => setModal("edit")}>
                Add note
              </Button>
              <Button
                startIcon={<Delete />}
                color="error"
                onClick={() => setModal("delete")}
              >
                Delete
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Metadata
          </Typography>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {formatDateTime(doc.created_at)} · {doc.created_by_user_id}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Last updated
              </Typography>
              <Typography variant="body2">{formatDateTime(doc.updated_at)}</Typography>
            </Grid>
            {doc.notes && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {doc.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Versions
            </Typography>
            <Chip size="small" label={`${doc.versions.length} total`} />
          </Box>
          {downloadError && (
            <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ mb: 2 }}>
              {downloadError}
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {doc.versions.map((v) => (
                  <TableRow key={v.id} hover>
                    <TableCell>
                      v{v.version_number}
                      {v.id === doc.current_version_id && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{v.file_name}</TableCell>
                    <TableCell>{formatBytes(v.file_size_bytes)}</TableCell>
                    <TableCell>{formatDateTime(v.uploaded_at)}</TableCell>
                    <TableCell>{v.creation_reason ?? "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(v.id)}
                        disabled={download.isPending}
                        aria-label="Download"
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Audit history (last 50)
          </Typography>
          <Stack divider={<Divider flexItem />} spacing={1}>
            {doc.audit_events.map((e) => (
              <Box key={e.id} sx={{ display: "flex", gap: 2, py: 1 }}>
                <Box sx={{ width: 200 }}>
                  <Typography variant="body2">
                    {DOCUMENT_AUDIT_EVENT_LABELS[e.event_type] ?? e.event_type}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(e.at)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {e.reason && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {e.reason}
                    </Typography>
                  )}
                  {e.actor_user_id && (
                    <Typography variant="caption" color="text.secondary">
                      by {e.actor_user_id}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
            {doc.audit_events.length === 0 && (
              <Typography color="text.secondary">No audit events recorded.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <ReasonPromptDialog
        open={modal === "edit"}
        title="Add audit note"
        description="Record a reason in the audit log. (Field-level edits land in a future iteration.)"
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          // v1: this dialog only records the reason in the audit log; no
          // field-level edits yet. update_metadata is called with reason-only,
          // which the service treats as a no-op (no audit event written when
          // nothing changed). A future iteration adds title/type/notes inputs.
          await update.mutateAsync({ reason });
        }}
      />

      <ReasonPromptDialog
        open={modal === "delete"}
        title="Soft-delete this document"
        description="Versions and S3 objects are kept for the audit trail; the document is hidden from default lists."
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          await remove.mutateAsync({ id: doc.id, payload: { reason } });
          navigate("/clinops/documents");
        }}
      />

      <ReasonPromptDialog
        open={modal === "restore"}
        title="Restore this document"
        confirmLabel="Restore"
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          await restore.mutateAsync({ id: doc.id, payload: { reason } });
        }}
      />
    </Box>
  );
}
